import {
  CONTACT_PAGE_SIZE,
  CONTACT_ROLE_FILTER,
} from '../components/messaging-routes/contacts-handler';
import {
  MESSAGING_API_LATEST_VERSION,
  MESSAGING_API_LEGACY_VERSION,
  MODEL_NAMES,
} from '../constants/index';
import { ROLES } from '../models/messaging-contact';
import { all, didCancel, task } from 'ember-concurrency';
import {
  conversationInfinityModel,
  pageNumberInfinityOptions,
} from '../utils/messaging/infinity-model';
import { convertToUser } from '../utils/messaging/user';
import { findConversationByContacts } from '../utils/messaging/conversation';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { testAwareTimeout } from 'ember-simplepractice/utils/waiters';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

const TOKEN_REFRESH_INTERVAL_MINS = 4.5;
const MESSAGING_UNAVAILABLE_ERROR = 'MessagingUnavailableError';

function messagingUnavailableMessage({ status, reason, redirected }) {
  const code = status ?? 'unknown';
  let detail = 'endpoint returned HTML';

  if (reason !== 'html') {
    detail = 'endpoint did not return JSON';
  } else if (redirected) {
    detail = 'endpoint redirected to HTML';
  } else if (status >= 500) {
    detail = 'endpoint failed';
  } else if (status >= 400) {
    detail = 'request was rejected';
  }

  return `Messaging token ${detail} (${code})`;
}

export class MessagingUnavailableError extends Error {
  constructor({ status, reason, redirected = false, cause } = {}) {
    super(messagingUnavailableMessage({ status, reason, redirected }), { cause });
    this.name = MESSAGING_UNAVAILABLE_ERROR;
    this.status = status;
    this.reason = reason;
    this.redirected = redirected;
  }
}

export function isMessagingUnavailable(error) {
  return error?.name === MESSAGING_UNAVAILABLE_ERROR;
}

export const CONVERSATION_SEARCH_MAX_LENGTH = 1000;
export const CONVERSATION_SEARCH_DEBOUNCE_MS = 200;

const optionDefaults = {
  clientMessagingEnabled: false,
  groupMessagingEnabled: false,
  hasTeamMembers: false,
  messagingApiUrl: '',
  trackEvent: () => {},
};

export default class MessagingService extends Service {
  @service infinity;
  @service intl;
  @service messagingLogging;
  @service messagingNavigation;
  @service messagingSession;
  @service('spds/toast') toast;
  @service store;

  @tracked conversations;
  @tracked currentProfile;
  @tracked options = { ...optionDefaults };
  @tracked search = '';
  @tracked settingAwayStatus = false;

  token;

  get messagingApiUrl() {
    return this.options.messagingApiUrl;
  }

  get trackEvent() {
    return this.options.trackEvent;
  }

  get hasTeamMembers() {
    return this.options.hasTeamMembers;
  }

  get clientMessagingEnabled() {
    return this.options.clientMessagingEnabled;
  }

  get searchableContactsText() {
    let translationKey = 'emc.contacts_page.search_placeholder.team_members';

    if (this.clientMessagingEnabled) {
      translationKey = this.hasTeamMembers
        ? 'emc.contacts_page.search_placeholder.clients_contacts_and_team_members'
        : 'emc.contacts_page.search_placeholder.clients_and_contacts';
    }

    return this.intl.t(translationKey);
  }

  get groupMessagingEnabled() {
    return this.options.groupMessagingEnabled;
  }

  get apiVersion() {
    return this.groupMessagingEnabled ? MESSAGING_API_LATEST_VERSION : MESSAGING_API_LEGACY_VERSION;
  }

  get tokenRefreshIntervalMins() {
    return TOKEN_REFRESH_INTERVAL_MINS;
  }

  get hasSearchTerm() {
    return Boolean(this.search?.trim());
  }

  get isLoadingConversations() {
    return (
      this.getConversations.isRunning ||
      (this.searchConversations.isRunning && !this.conversations?.length)
    );
  }

  loadApp = task({ drop: true }, async options => {
    this.options = { ...optionDefaults, ...options };
    this.messagingNavigation.setGroupMessagingEnabled(this.groupMessagingEnabled);
    await this.getMessagingToken.perform();
    await this.getCurrentProfile.perform();
    await this.getConversations.perform();

    if (this.currentProfile.isClient) {
      await this.populateVirtualConversations.perform();
      if (this.conversations.length === 1) {
        await this.messagingNavigation.toConversation(this.conversations.firstObject);
      }
    }

    this.pollMessagingToken.perform();
    this.messagingSession.start(this.messagingApiUrl);
  });

  getMessagingToken = task({ drop: true }, async () => {
    const response = await fetch('/messaging/tokens', {
      method: 'POST',
    });
    const json = await this.#readTokenPayload(response);
    this.token = json.token;
  });

  getCurrentProfile = task({ drop: true }, async () => {
    this.currentProfile = await this.store.queryRecord(MODEL_NAMES.PROFILE, {
      include: 'system_user',
    });

    let user = this.store
      .peekAll(MODEL_NAMES.USER)
      .find(user => user.id === this.currentProfile.id);
    if (!user) {
      user = this.store.push({
        data: {
          id: this.currentProfile.id,
          type: MODEL_NAMES.USER,
          attributes: {
            name: this.currentProfile.name,
            externalScope: this.currentProfile.externalScope,
            externalId: this.currentProfile.externalId,
            organizationId: this.currentProfile.organizationId,
          },
        },
      });
    }
    this.currentProfile.user = user;
  });

  getConversations = task({ restartable: true }, async () => {
    const searchValue = this.search?.trim();
    const filter = searchValue ? { search: searchValue } : undefined;

    this.conversations = await this.infinity.model(
      MODEL_NAMES.CONVERSATION,
      {
        include: 'users,last_message,last_message.user',
        ...(filter && { filter }),
        ...pageNumberInfinityOptions(),
      },
      conversationInfinityModel(this.currentProfile.isClient)
    );
  });

  searchConversations = task({ restartable: true }, async ({ debounce = true } = {}) => {
    if (debounce) {
      await testAwareTimeout(CONVERSATION_SEARCH_DEBOUNCE_MS);
    }

    try {
      await this.getConversations.perform();
    } catch (err) {
      if (didCancel(err)) return;
      this.messagingLogging.error(err, { context: 'conversation-search' });
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  updateSearch(value) {
    this.search = String(value ?? '').slice(0, CONVERSATION_SEARCH_MAX_LENGTH);
    this.searchConversations.perform();
  }

  clearSearch() {
    if (!this.search) return;
    this.updateSearch('');
  }

  removeConversationFromList(id) {
    const conversations = this.conversations;
    if (!conversations) {
      return;
    }

    const record = conversations.content.find(conv => conv.isSameId(id));
    this.removeConversationRecord(record);
  }

  removeConversationRecord(record) {
    const conversations = this.conversations;
    if (!conversations || !record) {
      return;
    }

    conversations.removeObject(record);
    // Reassign so @tracked consumers see the mutation.
    this.conversations = conversations;
  }

  replaceConversationInList(removeId, addRecord) {
    const conversations = this.conversations;
    if (!conversations) {
      return;
    }

    const list = conversations.content;
    const removeIndex = list.findIndex(conv => conv.isSameId(removeId));
    const existingIndex = list.findIndex(conv => conv.isSameId(addRecord.id));
    const alreadyInList = existingIndex >= 0;

    if (removeIndex < 0) {
      if (alreadyInList) {
        return;
      }

      conversations.unshiftObjects([addRecord]);
      this.conversations = conversations;
      return;
    }

    if (alreadyInList && removeIndex !== existingIndex) {
      conversations.removeAt(removeIndex);
    } else {
      conversations.replace(removeIndex, 1, [addRecord]);
    }

    this.conversations = conversations;
  }

  populateVirtualConversations = task({ drop: true }, async () => {
    const contacts = await this.store.query(MODEL_NAMES.CONTACT, {
      filter: CONTACT_ROLE_FILTER,
      include: 'couple',
      page: { number: 1, size: CONTACT_PAGE_SIZE },
    });

    if (!contacts) {
      return;
    }

    const contactsWithoutConversations = contacts.filter(
      contact => !findConversationByContacts(this.conversations, contact.members)
    );

    const virtualConversations = contactsWithoutConversations.map(contact =>
      this.#createConversation(
        contact.members,
        contact.role === ROLES.forCouple && contact.members.length === 2
      )
    );
    this.conversations.unshiftObjects(virtualConversations);
  });

  findOrCreateConversationWithContacts(contacts) {
    const existingConversation = findConversationByContacts(this.conversations, contacts);

    if (existingConversation) {
      return existingConversation;
    }

    return this.#createConversation(contacts);
  }

  async findOrCreateConversationByClientIds(clientExternalIds) {
    const contacts = await all(
      clientExternalIds.map(externalId =>
        this.store.adapterFor(MODEL_NAMES.CONTACT).fetchContact(externalId)
      )
    );

    return this.findOrCreateConversationWithContacts(contacts);
  }

  refreshProfileAndConversations = task({ drop: true }, async () => {
    return all([this.getCurrentProfile.perform(), this.getConversations.perform()]);
  });

  pollMessagingToken = task(async () => {
    do {
      await testAwareTimeout(this.tokenRefreshIntervalMins * 60 * 1000);

      try {
        await this.getMessagingToken.perform();
      } catch (error) {
        if (!isMessagingUnavailable(error)) {
          throw error;
        }
      }
    } while (!isEmberTesting());
  });

  async #readTokenPayload(response) {
    const body = await response.text();
    const details = {
      status: response.status,
      redirected: response.redirected,
    };

    if (body.trimStart().startsWith('<')) {
      throw new MessagingUnavailableError({ ...details, reason: 'html' });
    }

    if (!response.ok) {
      throw new Error(`Messaging token request failed (${response.status})`);
    }

    try {
      return JSON.parse(body);
    } catch (error) {
      throw new MessagingUnavailableError({ ...details, reason: 'invalid-json', cause: error });
    }
  }

  #createConversation = (contacts, isCouple = false) => {
    const users = [this.currentProfile?.user, ...contacts.map(c => convertToUser(c, this.store))];
    return this.store.createRecord(MODEL_NAMES.CONVERSATION, {
      users,
      isCouple,
    });
  };
}
